import {
  type ContentBlock,
  type MarkdownSegment,
  parseMarkdownBlocks,
  parseToVisibleSegments,
} from './reflectionUtils';

export const REFLECTION_READER_CONTRACT = 'frontend-reflection-legacy-v1';
export const REFLECTION_ANCHOR_CONTRACT = 'reflection-keep-anchor-v1';
export const SAVED_NOTE_UNLOCATED = 'saved-note-unlocated';

export type ReflectionSourceIdentity = {
  revision: number;
  readerContract: typeof REFLECTION_READER_CONTRACT;
  anchorContract: typeof REFLECTION_ANCHOR_CONTRACT;
  scope: 'whole-block' | 'selection';
};

export type ReflectionStoredSourceIdentity = ReflectionSourceIdentity & {
  blockId: string;
  startIndex: number;
  endIndex: number;
};

export type ReflectionProjectedBlock = ContentBlock & {
  segments: MarkdownSegment[];
  visibleText: string;
};

// This wrapper deliberately does not reinterpret Markdown. The existing reader
// owns block structure and visible UTF-16 text, including its legacy quirks.
export const projectReflection = (
  markdown: string,
): ReflectionProjectedBlock[] =>
  parseMarkdownBlocks(markdown).map((block) => {
    const segments = parseToVisibleSegments(block.text);
    return {
      ...block,
      segments,
      visibleText: segments.map((segment) => segment.text).join(''),
    };
  });

const hasLoneSurrogate = (text: string): boolean => {
  for (let index = 0; index < text.length; index += 1) {
    const unit = text.charCodeAt(index);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = text.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) {
        return true;
      }
      index += 1;
    } else if (unit >= 0xdc00 && unit <= 0xdfff) {
      return true;
    }
  }
  return false;
};

export const isReflectionUTF16Boundary = (
  text: string,
  index: number,
): boolean => {
  if (!Number.isInteger(index) || index < 0 || index > text.length) {
    return false;
  }
  const before = text.charCodeAt(index - 1);
  const after = text.charCodeAt(index);
  return !(
    before >= 0xd800 &&
    before <= 0xdbff &&
    after >= 0xdc00 &&
    after <= 0xdfff
  );
};

export type ReflectionAnchor = {
  blockId: string;
  startIndex: number;
  endIndex: number;
  selectedText: string;
  // Verifier inputs can originate in untrusted wire records, not just typed UI.
  scope: string;
};

// A verifier, not a fallback resolver: malformed/noncanonical or changed
// snapshots stay unlocated. Controls never enter these body-owned roots.
export const verifyReflectionAnchor = (
  blocks: ReflectionProjectedBlock[],
  anchor: ReflectionAnchor,
): boolean => {
  if (hasLoneSurrogate(anchor.selectedText)) {
    return false;
  }
  const keys = anchor.blockId.split(':');
  if (
    keys.length > 2 ||
    keys.some((key) => !/^block-(0|[1-9]\d*)$/u.test(key))
  ) {
    return false;
  }
  const start = blocks.findIndex((block) => block.id === keys[0]);
  const end = blocks.findIndex((block) => block.id === (keys[1] ?? keys[0]));
  if (start < 0 || end < start || (keys.length === 2 && end === start)) {
    return false;
  }
  const first = blocks[start];
  const last = blocks[end];
  if (!first || !last) {
    return false;
  }
  if (
    !isReflectionUTF16Boundary(first.visibleText, anchor.startIndex) ||
    !isReflectionUTF16Boundary(last.visibleText, anchor.endIndex) ||
    (start === end && anchor.startIndex >= anchor.endIndex) ||
    (start !== end &&
      (anchor.startIndex === first.visibleText.length || anchor.endIndex === 0))
  ) {
    return false;
  }
  const passage = blocks
    .slice(start, end + 1)
    .map((block, index) =>
      block.visibleText.slice(
        index === 0 ? anchor.startIndex : 0,
        index === end - start ? anchor.endIndex : undefined,
      ),
    )
    .join('\n\n');
  if (hasLoneSurrogate(passage) || passage !== anchor.selectedText) {
    return false;
  }
  if (anchor.scope === 'whole-block') {
    return (
      start === end &&
      first.type !== 'heading' &&
      anchor.startIndex === 0 &&
      anchor.endIndex === first.visibleText.length &&
      passage.trim().length > 0
    );
  }
  return (
    anchor.scope === 'selection' &&
    passage.length >= 5 &&
    passage === passage.trim()
  );
};
